// =============================================================================
// DATA MODELER - Main component with React Flow
// =============================================================================

import { useCallback, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { PrimitivePalette } from './components/PrimitivePalette';
import { EntityConfigPanel } from './components/EntityConfigPanel';
import { ModuleAIPrompt } from '../../components/ModuleAIPrompt';
import { nodeTypes } from './components/nodes';
import { edgeTypes } from './components/edges';
import { useDataModelerStore } from './store';
import type { PrimitiveType } from './types';

const defaultEdgeOptions = {
  type: 'relationEdge',
  animated: false,
};

export function DataModeler() {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const reactFlowInstance = useRef<any>(null);
  const [isAIProcessing, setIsAIProcessing] = useState(false);

  const {
    nodes,
    edges,
    selectedEntityId,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addEntity,
    selectEntity,
    removeEntity,
    duplicateEntity,
    removeRelation,
  } = useDataModelerStore();

  // AI prompt execution handler for data modeler
  const handleAIExecute = useCallback(async (prompt: string) => {
    setIsAIProcessing(true);
    try {
      const lowerPrompt = prompt.toLowerCase();

      // Calculate position for new entities
      const getNextPosition = () => {
        const lastNode = nodes[nodes.length - 1];
        if (lastNode) {
          return { x: lastNode.position.x + 250, y: lastNode.position.y };
        }
        return { x: 150, y: 100 };
      };

      // Pattern: create X entities
      const entityCountMatch = lowerPrompt.match(/(\d+)\s*entités?|(\d+)\s*entities/);
      if (entityCountMatch) {
        const countStr = entityCountMatch[1] ?? entityCountMatch[2];
        if (countStr) {
          const count = parseInt(countStr, 10);
          const primitiveTypes: PrimitiveType[] = ['person', 'organization', 'document', 'asset', 'transaction'];
          for (let i = 0; i < Math.min(count, 6); i++) {
            const pos = { x: 150 + (i % 3) * 280, y: 100 + Math.floor(i / 3) * 250 };
            const primitiveType = primitiveTypes[i % primitiveTypes.length];
            if (primitiveType) {
              addEntity(primitiveType, pos);
            }
          }
          return;
        }
      }

      // Pattern: add specific entity types
      const pos = getNextPosition();
      if (lowerPrompt.includes('personne') || lowerPrompt.includes('person') || lowerPrompt.includes('utilisateur') || lowerPrompt.includes('user')) {
        addEntity('person', pos);
      } else if (lowerPrompt.includes('organisation') || lowerPrompt.includes('organization') || lowerPrompt.includes('entreprise') || lowerPrompt.includes('company')) {
        addEntity('organization', pos);
      } else if (lowerPrompt.includes('document') || lowerPrompt.includes('fichier') || lowerPrompt.includes('file')) {
        addEntity('document', pos);
      } else if (lowerPrompt.includes('asset') || lowerPrompt.includes('ressource') || lowerPrompt.includes('actif')) {
        addEntity('asset', pos);
      } else if (lowerPrompt.includes('transaction') || lowerPrompt.includes('paiement') || lowerPrompt.includes('payment')) {
        addEntity('transaction', pos);
      } else if (lowerPrompt.includes('produit') || lowerPrompt.includes('product')) {
        addEntity('asset', pos); // Use asset for products
      } else if (lowerPrompt.includes('commande') || lowerPrompt.includes('order')) {
        addEntity('transaction', pos); // Use transaction for orders
      } else if (lowerPrompt.includes('client') || lowerPrompt.includes('customer')) {
        addEntity('person', pos); // Use person for customers
      } else {
        // Default: add a document entity
        addEntity('document', pos);
      }

      await new Promise(resolve => setTimeout(resolve, 500));
    } finally {
      setIsAIProcessing(false);
    }
  }, [nodes, addEntity]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onInit = useCallback((instance: any) => {
    reactFlowInstance.current = instance;
  }, []);

  const onDragStart = useCallback(
    (event: React.DragEvent, primitive: PrimitiveType) => {
      event.dataTransfer.setData('application/datamodeler', primitive);
      event.dataTransfer.effectAllowed = 'move';
    },
    []
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const primitive = event.dataTransfer.getData('application/datamodeler') as PrimitiveType;

      if (!primitive || !reactFlowWrapper.current || !reactFlowInstance.current) {
        return;
      }

      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.current.screenToFlowPosition({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      addEntity(primitive, position);
    },
    [addEntity]
  );

  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      selectEntity(node.id);
    },
    [selectEntity]
  );

  const onEdgeClick = useCallback(
    (_event: React.MouseEvent, edge: { id: string }) => {
      // Select relation for editing
      useDataModelerStore.getState().selectRelation(edge.id);
    },
    []
  );

  const onPaneClick = useCallback(() => {
    selectEntity(null);
  }, [selectEntity]);

  // Enhance nodes with callbacks
  const enhancedNodes = nodes.map((node) => ({
    ...node,
    data: {
      ...node.data,
      onDelete: removeEntity,
      onDuplicate: duplicateEntity,
    },
  }));

  // Enhance edges with delete callback
  const enhancedEdges = edges.map((edge) => ({
    ...edge,
    data: {
      ...edge.data,
      onDelete: removeRelation,
    },
  }));

  return (
    <div className="flex h-full">
      {/* Left sidebar - Primitive palette with AI prompt */}
      <div className="w-80 border-r border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-900 flex flex-col overflow-hidden">
        {/* AI Prompt at top */}
        <div className="p-3 border-b border-secondary-200 dark:border-secondary-700">
          <ModuleAIPrompt
            moduleType="data"
            onExecute={handleAIExecute}
            isProcessing={isAIProcessing}
            compact
          />
        </div>
        {/* Primitive palette below */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <PrimitivePalette onDragStart={onDragStart} />
        </div>
      </div>

      {/* Main canvas */}
      <div ref={reactFlowWrapper} className="flex-1">
        <ReactFlow
          nodes={enhancedNodes}
          edges={enhancedEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={onInit}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          fitView
          snapToGrid
          snapGrid={[15, 15]}
          className="bg-secondary-50 dark:bg-secondary-950"
          connectionLineStyle={{ stroke: '#6B7280', strokeWidth: 2 }}
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={20}
            size={1}
            className="bg-secondary-50 dark:bg-secondary-950"
          />
          <Controls className="bg-white dark:bg-secondary-800 border-secondary-200 dark:border-secondary-700" />
          <MiniMap
            className="bg-white dark:bg-secondary-800 border-secondary-200 dark:border-secondary-700"
            nodeColor={(node) => {
              const data = node.data as unknown as { color?: string };
              return data.color || '#6B7280';
            }}
          />
        </ReactFlow>
      </div>

      {/* Right sidebar - Entity config panel */}
      {selectedEntityId && (
        <div className="w-80 border-l border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-900 overflow-y-auto overflow-x-hidden">
          <EntityConfigPanel />
        </div>
      )}
    </div>
  );
}
